import * as XLSX from 'xlsx';
import ExcelJS from 'exceljs';
import { MenuData } from '../types';

// Helper to trigger download
const saveBuffer = (buffer: ArrayBuffer, filename: string) => {
  const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  const url = window.URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = `${filename}.xlsx`;
  anchor.click();
  window.URL.revokeObjectURL(url);
};

type VariantColumn = {
  aggKey: string;
  variantKey: string;
  header?: string;
};

type ImageFilenameMap = Record<string, { original?: string; variants?: Record<string, string> }>;

type ExportOptions = {
  includeImagesColumn?: boolean;
  variantColumns?: VariantColumn[];
  imageFilenames?: ImageFilenameMap;
  returnBuffer?: boolean;
  workbook?: ExcelJS.Workbook;
  sheetName?: string;
};

export const exportToExcel = async (
  data: MenuData,
  filename: string = 'menu-export',
  currency: string = 'QAR',
  options: ExportOptions = {}
) => {
  const workbook = options.workbook || new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet(options.sheetName || 'Menu Data');

  // --- Colors ---
  const YELLOW_HEX = "FFF200";
  const BLACK_HEX = "000000";
  const WHITE_HEX = "FFFFFF";

  // --- Styles ---
  const borderStyle: Partial<ExcelJS.Borders> = {
    top: { style: 'thin', color: { argb: BLACK_HEX } },
    left: { style: 'thin', color: { argb: BLACK_HEX } },
    bottom: { style: 'thin', color: { argb: BLACK_HEX } },
    right: { style: 'thin', color: { argb: BLACK_HEX } }
  };

  const baseFont = { name: 'Arial', size: 10, color: { argb: BLACK_HEX } };
  const whiteFont = { name: 'Arial', size: 10, color: { argb: WHITE_HEX }, bold: true };
  const boldFont = { name: 'Arial', size: 11, color: { argb: BLACK_HEX }, bold: true };

  // --- Columns Setup ---
  const baseColumns: ExcelJS.Column[] = [
    { header: 'Category - EN', key: 'catEn', width: 25 },
    { header: 'Category - AR', key: 'catAr', width: 25 },
    { header: 'Item Name - EN', key: 'itemEn', width: 30 },
    { header: 'Item Name - AR', key: 'itemAr', width: 30 },
    { header: 'Description - EN', key: 'descEn', width: 50 },
    { header: 'Description - AR', key: 'descAr', width: 40 },
    { header: 'Item Price', key: 'price', width: 15 },
    { header: 'Max', key: 'max', width: 10 },
    { header: 'Min', key: 'min', width: 10 }
  ];

  const extraColumns: ExcelJS.Column[] = [];
  if (options.includeImagesColumn) {
    extraColumns.push({ header: 'Image Original Filename', key: 'imgOriginal', width: 40 });
  }
  if (options.variantColumns && options.variantColumns.length) {
    options.variantColumns.forEach((vc) => {
      extraColumns.push({
        header: vc.header || `image_${vc.aggKey}_${vc.variantKey}_filename`,
        key: `img_${vc.aggKey}_${vc.variantKey}`,
        width: 40,
      });
    });
  }

  worksheet.columns = [...baseColumns, ...extraColumns];
  const lastColumnIndex = worksheet.columns.length;

  // --- Header Styling ---
  const headerRow = worksheet.getRow(1);
  headerRow.eachCell((cell) => {
    cell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: BLACK_HEX }
    };
    cell.font = { ...whiteFont, size: 11 };
    cell.alignment = { vertical: 'middle', horizontal: 'center' };
    cell.border = {
        top: { style: 'medium', color: { argb: BLACK_HEX } },
        left: { style: 'medium', color: { argb: BLACK_HEX } },
        bottom: { style: 'medium', color: { argb: BLACK_HEX } },
        right: { style: 'medium', color: { argb: BLACK_HEX } }
    };
  });

  let currentRowIndex = 2;

  data.categories.forEach(cat => {
    if (cat.items.length === 0) {
        // Empty Category
        const row = worksheet.getRow(currentRowIndex);
        row.getCell(1).value = cat.name.en;
        row.getCell(2).value = cat.name.ar || '';
        
        // Style Category Cells (Yellow)
        [1, 2].forEach(c => {
            const cell = row.getCell(c);
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: YELLOW_HEX } };
            cell.font = boldFont;
            cell.border = borderStyle;
            cell.alignment = { vertical: 'middle', horizontal: c === 1 ? 'left' : 'right' };
        });

        currentRowIndex++;
    } else {
        cat.items.forEach((item, iIdx) => {
            // Calculate how many rows this item takes (1 for item + choice groups + options)
            let itemRowsCount = 1;
            item.choiceGroups.forEach(cg => {
                itemRowsCount += 1 + cg.options.length; // 1 for header, N for options
            });

            const startRow = currentRowIndex;
            
            // 1. Item Row
            const itemRow = worksheet.getRow(currentRowIndex);
            
            // Only show Category on the first item of the category
            if (iIdx === 0) {
                itemRow.getCell(1).value = cat.name.en;
                itemRow.getCell(2).value = cat.name.ar || '';
                
                [1, 2].forEach(c => {
                    const cell = itemRow.getCell(c);
                    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: YELLOW_HEX } };
                    cell.font = boldFont;
                    cell.border = borderStyle;
                    cell.alignment = { vertical: 'middle', horizontal: c === 1 ? 'left' : 'right' };
                });
            } else {
                // Empty cells for subsequent items in same category
                // Explicitly clear borders/fills if needed, but default is empty
            }

            itemRow.getCell(3).value = item.name.en;
            itemRow.getCell(4).value = item.name.ar || '';
            itemRow.getCell(5).value = item.description.en;
            itemRow.getCell(6).value = item.description.ar || '';
            itemRow.getCell(7).value = `${currency} ${item.price.toFixed(2)}`;

            // Image filename columns (original + variants)
            let colCursor = 10;
            if (options.includeImagesColumn) {
              const original = options.imageFilenames?.[item.id]?.original || '';
              itemRow.getCell(colCursor).value = original;
              colCursor++;
            }
            if (options.variantColumns && options.variantColumns.length) {
              const variantMap = options.imageFilenames?.[item.id]?.variants || {};
              options.variantColumns.forEach((vc, idx) => {
                const targetCol = colCursor + idx;
                itemRow.getCell(targetCol).value = variantMap[`${vc.aggKey}_${vc.variantKey}`] || '';
              });
              colCursor += options.variantColumns.length;
            }

            // Style Item Name (Yellow)
            [3, 4].forEach(c => {
                const cell = itemRow.getCell(c);
                cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: YELLOW_HEX } };
                cell.font = baseFont;
                cell.border = borderStyle;
                cell.alignment = { vertical: 'middle', horizontal: 'center' };
            });

            // Style Description & Price (Yellow)
            [5, 6, 7, 8, 9].forEach(c => {
                const cell = itemRow.getCell(c);
                cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: YELLOW_HEX } };
                cell.font = baseFont;
                cell.border = borderStyle;
                cell.alignment = { vertical: 'middle', horizontal: c === 7 ? 'center' : (c === 6 ? 'right' : 'left') };
            });

            // Apply borders for any extra columns on the item row
            for (let c = 10; c <= lastColumnIndex; c++) {
              const cell = itemRow.getCell(c);
              cell.border = borderStyle;
              cell.alignment = { vertical: 'middle', horizontal: 'left' };
            }

            currentRowIndex++;

            // 2. Choice Groups
            item.choiceGroups.forEach(cg => {
                const groupRow = worksheet.getRow(currentRowIndex);
                
                // Group Header Data
                groupRow.getCell(5).value = cg.name.en;
                groupRow.getCell(6).value = cg.name.ar || 'إضافات';
                groupRow.getCell(8).value = cg.max;
                groupRow.getCell(9).value = cg.min;

                // Style Group Header (Black Bar)
                for (let c = 5; c <= 9; c++) {
                    const cell = groupRow.getCell(c);
                    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: BLACK_HEX } };
                    cell.font = whiteFont;
                    cell.border = borderStyle;
                    cell.alignment = { vertical: 'middle', horizontal: c >= 8 ? 'center' : (c === 6 ? 'right' : 'left') };
                }
                
                // Ensure Item Name columns are still colored Yellow (for the merge effect visual)
                [3, 4].forEach(c => {
                    const cell = groupRow.getCell(c);
                    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: YELLOW_HEX } };
                    cell.border = borderStyle;
                });

                // Extra columns styling (blank but bordered)
                for (let c = 10; c <= lastColumnIndex; c++) {
                    const cell = groupRow.getCell(c);
                    cell.border = borderStyle;
                }

                currentRowIndex++;

                // 3. Options
                cg.options.forEach(opt => {
                    const optRow = worksheet.getRow(currentRowIndex);
                    optRow.getCell(5).value = opt.name.en;
                    optRow.getCell(6).value = opt.name.ar || '';
                    optRow.getCell(7).value = opt.priceModifier ? `${currency} ${opt.priceModifier.toFixed(2)}` : `${currency} -`;

                    // Style Options (White)
                    for (let c = 5; c <= 9; c++) {
                        const cell = optRow.getCell(c);
                        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: WHITE_HEX } };
                        cell.font = baseFont;
                        cell.border = borderStyle;
                        cell.alignment = { vertical: 'middle', horizontal: c === 7 ? 'center' : (c === 6 ? 'right' : 'left') };
                    }

                    // Ensure Item Name columns are still colored Yellow
                    [3, 4].forEach(c => {
                        const cell = optRow.getCell(c);
                        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: YELLOW_HEX } };
                        cell.border = borderStyle;
                    });

                    // Extra columns styling (blank but bordered)
                    for (let c = 10; c <= lastColumnIndex; c++) {
                        const cell = optRow.getCell(c);
                        cell.border = borderStyle;
                    }

                    currentRowIndex++;
                });
            });

            // Merge Item Name Cells Vertically
            const endRow = currentRowIndex - 1;
            if (endRow > startRow) {
                worksheet.mergeCells(startRow, 3, endRow, 3); // Item Name EN
                worksheet.mergeCells(startRow, 4, endRow, 4); // Item Name AR
            }
        });
    }
  });

  if (!options.workbook) {
    const buffer = await workbook.xlsx.writeBuffer();
    if (options.returnBuffer) {
      return buffer;
    }
    saveBuffer(buffer, filename);
  }
};

export const exportToExcelTemplate2 = async (
  data: MenuData,
  filename: string = 'menu-export-v2',
  currency: string = 'QAR',
  options: ExportOptions = {}
) => {
  const workbook = options.workbook || new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet(options.sheetName || 'Menu Data (V2)');

  // --- Colors ---
  const PURPLE_HEX = "6f42c1"; // Purple
  const WHITE_HEX = "FFFFFF";
  const LIGHT_GRAY_HEX = "F3F4F6";
  const BLACK_HEX = "000000";

  // --- Styles ---
  const borderStyle: Partial<ExcelJS.Borders> = {
    top: { style: 'thin', color: { argb: 'E5E7EB' } },
    left: { style: 'thin', color: { argb: 'E5E7EB' } },
    bottom: { style: 'thin', color: { argb: 'E5E7EB' } },
    right: { style: 'thin', color: { argb: 'E5E7EB' } }
  };

  const headerFont = { name: 'Arial', size: 10, color: { argb: WHITE_HEX }, bold: true };
  const baseFont = { name: 'Arial', size: 10, color: { argb: '374151' } };

  // --- Columns Setup ---
  const columns: ExcelJS.Column[] = [
    { header: 'Category', key: 'category', width: 25 },
    { header: 'Item Name', key: 'itemName', width: 30 },
    { header: 'Description', key: 'description', width: 40 },
    { header: 'Price', key: 'price', width: 15 },
    { header: 'Option Group', key: 'optGroup', width: 25 },
    { header: 'Option Name', key: 'optName', width: 25 },
    { header: 'Option Price', key: 'optPrice', width: 15 }
  ];

  worksheet.columns = columns;

  // --- Header Styling ---
  const headerRow = worksheet.getRow(1);
  headerRow.eachCell((cell) => {
    cell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: PURPLE_HEX }
    };
    cell.font = headerFont;
    cell.alignment = { vertical: 'middle', horizontal: 'left' };
    cell.border = borderStyle;
  });

  let currentRowIndex = 2;

  data.categories.forEach(cat => {
    cat.items.forEach(item => {
        const startRow = currentRowIndex;
        
        // Item Row
        const itemRow = worksheet.getRow(currentRowIndex);
        itemRow.getCell(1).value = cat.name.en;
        itemRow.getCell(2).value = item.name.en;
        itemRow.getCell(3).value = item.description.en;
        itemRow.getCell(4).value = item.price;

        // Style Item Cells
        [1, 2, 3, 4].forEach(c => {
            const cell = itemRow.getCell(c);
            cell.font = { ...baseFont, bold: true };
            cell.alignment = { vertical: 'top', horizontal: 'left' };
            cell.border = borderStyle;
        });

        // If no choice groups, just move to next row
        if (item.choiceGroups.length === 0) {
            currentRowIndex++;
        } else {
            // Choice Groups
            item.choiceGroups.forEach(cg => {
                cg.options.forEach((opt, optIdx) => {
                    const row = worksheet.getRow(currentRowIndex);
                    
                    // Fill item info for first row of options if we want full table, 
                    // or leave blank for visual grouping. Let's fill for data integrity.
                    if (currentRowIndex > startRow) {
                         row.getCell(1).value = cat.name.en;
                         row.getCell(2).value = item.name.en;
                    }

                    row.getCell(5).value = cg.name.en;
                    row.getCell(6).value = opt.name.en;
                    row.getCell(7).value = opt.priceModifier;

                    // Style Option Cells
                    [1, 2, 3, 4, 5, 6, 7].forEach(c => {
                        const cell = row.getCell(c);
                        cell.font = baseFont;
                        cell.alignment = { vertical: 'top', horizontal: 'left' };
                        cell.border = borderStyle;
                    });

                    currentRowIndex++;
                });
            });
        }
    });
  });

  if (!options.workbook) {
    const buffer = await workbook.xlsx.writeBuffer();
    if (options.returnBuffer) {
      return buffer;
    }
    saveBuffer(buffer, filename);
  }
};

export const exportBothTemplates = async (
  data: MenuData,
  filename: string = 'menu-export-combined',
  currency: string = 'QAR'
) => {
  const workbook = new ExcelJS.Workbook();
  
  // Sheet 1: Standard
  await exportToExcel(data, filename, currency, { workbook, sheetName: 'Template 1 (Standard)' });
  
  // Sheet 2: New Layout
  await exportToExcelTemplate2(data, filename, currency, { workbook, sheetName: 'Template 2 (New Layout)' });

  const buffer = await workbook.xlsx.writeBuffer();
  saveBuffer(buffer, filename);
};

export const importFromExcel = async (file: File): Promise<MenuData> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target?.result as ArrayBuffer);
        const workbook = XLSX.read(data, { type: 'array' });
        const sheetName = workbook.SheetNames[0];
        const sheet = workbook.Sheets[sheetName];
        
        // Use header:1 to get raw array of arrays
        const jsonData = XLSX.utils.sheet_to_json(sheet, { header: 1 });

        const menuData: MenuData = { 
            title: { en: 'MENU', ar: 'قائمة الطعام' }, // Default Title
            categories: [] 
        };
        
        let currentCat: any = null;
        let currentItem: any = null;
        let currentGroup: any = null;

        // Skip header row (index 0)
        for (let i = 1; i < jsonData.length; i++) {
          const row: any = jsonData[i];
          if (!row || row.length === 0) continue;

          const catEn = row[0];
          const itemEn = row[2];
          const descOrGroupOrOptEn = row[4];
          
          if (catEn) {
            // Handle denormalized data: only create new category if name changes
            if (!currentCat || currentCat.name.en !== catEn) {
                currentCat = {
                  id: `cat-${Date.now()}-${i}`,
                  name: { en: catEn, ar: row[1] || '' },
                  items: []
                };
                menuData.categories.push(currentCat);
                currentItem = null;
                currentGroup = null;
            }
          }

          if (itemEn && currentCat) {
            // Handle denormalized data: only create new item if name changes
            if (!currentItem || currentItem.name.en !== itemEn) {
                let price = 0;
                if (row[6]) {
                   const priceStr = String(row[6]).replace(/[^0-9.]/g, '');
                   price = parseFloat(priceStr) || 0;
                }

                currentItem = {
                  id: `item-${Date.now()}-${i}`,
                  name: { en: itemEn, ar: row[3] || '' },
                  description: { en: row[4] || '', ar: row[5] || '' }, // Main desc uses same col 4
                  price: price,
                  choiceGroups: []
                };
                currentCat.items.push(currentItem);
                currentGroup = null;
                continue; 
            }
            // If item name is same as current, treat as continuation (for denormalized group/option rows)
          }

          if (descOrGroupOrOptEn && currentItem) {
             const maxVal = row[7];
             const minVal = row[8];
             const priceVal = row[6];

             if (maxVal !== undefined || minVal !== undefined) {
                 currentGroup = {
                     id: `cg-${Date.now()}-${i}`,
                     name: { en: descOrGroupOrOptEn, ar: row[5] || '' },
                     min: parseInt(minVal) || 0,
                     max: parseInt(maxVal) || 1,
                     required: (parseInt(minVal) || 0) > 0,
                     optional: (parseInt(minVal) || 0) === 0,
                     options: []
                 };
                 currentItem.choiceGroups.push(currentGroup);
             } 
             else if (currentGroup) {
                 let optPrice = 0;
                 if (priceVal) {
                     const pStr = String(priceVal).replace(/[^0-9.]/g, '');
                     optPrice = parseFloat(pStr) || 0;
                 }
                 
                 currentGroup.options.push({
                     id: `opt-${Date.now()}-${i}`,
                     name: { en: descOrGroupOrOptEn, ar: row[5] || '' },
                     priceModifier: optPrice
                 });
             }
          }
        }
        resolve(menuData);
      } catch (err) {
        console.error("Import Error:", err);
        reject(err);
      }
    };
    reader.readAsArrayBuffer(file);
  });
};
